import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Alert, Collapse, Input, Modal, Radio, Spin, Tag, message } from 'antd'
import {
  getMyVibeProjectDetail,
  retryMyVibeProject,
  saveMyVibeProjectAccessType,
  saveMyVibeProjectDescription
} from '../../../request'
import VibeProjectDeleteDialog from '../../Profile/VibeProjectDeleteDialog'

// 与个人中心 Vibe 项目详情保持一致（Profile.js renderStatusTag）
const PROJECT_STATUS_META = {
  approved: { color: 'green', label: '已创建' },
  provisioning: { color: 'processing', label: '接入中' },
  ready: { color: 'green', label: '已就绪' },
  failed: { color: 'red', label: '接入失败' },
  deleting: { color: 'red', label: '删除中' },
  disabled: { color: 'default', label: '已停用' },
  deleted: { color: 'default', label: '已删除' }
}

const statusMetaOf = status => PROJECT_STATUS_META[status] || { color: 'default', label: status || '未返回状态' }

// 提示词在接口里可能以转义字符串存储，展示前统一还原换行与引号。
const formatInstructionText = text => {
  if (!text) return ''
  return String(text)
    .replace(/\\r\\n/g, '\n')
    .replace(/\\n/g, '\n')
    .replace(/\\"/g, '"')
}

/**
 * 操作 tab「维护操作」分区（仅项目 Owner 可见，P3；P6 适配 Vibe 应用项目）。
 *
 * 看板详情接口不含简介/访问对象/接入失败原因/初始化提示词，也不含项目生命周期状态
 * （detail.status 是数据集状态），所以本分区在 owner 视角下额外调
 * getMyVibeProjectDetail({ id }) 补充数据；该接口失败只降级本分区，不影响其它分区。
 * 写接口后端仍会二次校验 Owner，这里的隐藏只是体验优化。
 */
export default function MaintenanceSection ({ projectId, isOwner, projectKind = 'dataset', detail, onUpdated, onDeleted, onClose }) {
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState(false)
  const [project, setProject] = useState(null)
  const [descriptionEditing, setDescriptionEditing] = useState(false)
  const [descriptionDraft, setDescriptionDraft] = useState('')
  const [descriptionSaving, setDescriptionSaving] = useState(false)
  const [accessTypeSaving, setAccessTypeSaving] = useState(false)
  const [retryLoading, setRetryLoading] = useState(false)
  const [deleteVisible, setDeleteVisible] = useState(false)
  const mountedRef = useRef(true)

  const hasProjectId = projectId !== undefined && projectId !== null && projectId !== ''
  const source = detail || {}

  useEffect(() => {
    mountedRef.current = true
    return () => { mountedRef.current = false }
  }, [])

  const loadDetail = useCallback(() => {
    if (!isOwner || !hasProjectId) return
    setLoading(true)
    setLoadError(false)
    getMyVibeProjectDetail({ id: projectId })
      .then(result => {
        if (!mountedRef.current) return
        setProject(result && typeof result === 'object' ? result : null)
        setLoading(false)
      })
      .catch(() => {
        if (!mountedRef.current) return
        setLoading(false)
        setLoadError(true)
      })
  }, [hasProjectId, isOwner, projectId])

  useEffect(() => {
    loadDetail()
  }, [loadDetail])

  // 普通用户不渲染本分区（OperationsPanel 也只在 owner 时挂载）。
  if (!isOwner) return null

  if (!hasProjectId) {
    return <section className="term-ops-section">
      <div className="term-ops-head">
        <span className="term-ops-title">维护操作</span>
      </div>
      <div className="term-ops-card">
        <div className="term-ops-note">缺少项目信息，无法加载维护操作。</div>
      </div>
    </section>
  }

  const head = <div className="term-ops-head">
    <span className="term-ops-title">维护操作</span>
    <span className="term-ops-desc">仅项目 Owner 可见；写操作后端会二次校验，接口失败只影响本分区。</span>
  </div>

  if (loading || loadError) {
    return <section className="term-ops-section">
      {head}
      <div className="term-ops-card">
        {loadError
          ? <Alert
            type="error"
            showIcon
            message="维护操作加载失败"
            description="项目详情补充数据加载失败，不影响其它分区。"
            action={<button type="button" className="term-link" onClick={loadDetail}>重试</button>}
          />
          : <div className="term-ops-loading"><Spin size="small" /> 加载项目详情…</div>}
      </div>
    </section>
  }

  const description = project && project.description ? String(project.description) : ''
  const accessType = String((project && project.accessType) || 'INTERNAL').toUpperCase()
  // 看板详情 detail.status 是数据集状态（t_vibe_dataset.status），项目接入状态以
  // getMyVibeProjectDetail 为准；detail.status 只作为老接口未下发时的兜底。
  const status = String((project && project.status) || source.status || '').toLowerCase()
  const failed = status === 'failed'
  // P6：访问对象（内部/外部）与重试接入只对 Vibe 应用项目开放；AIC 数据集无这两个维度。
  const isApplication = projectKind === 'application'
  const statusMeta = statusMetaOf(status)
  const provisionError = project && project.provisionError ? String(project.provisionError) : ''
  const mysqlDatabaseError = project && project.mysqlDatabaseError ? String(project.mysqlDatabaseError) : ''
  const instruction = formatInstructionText(project && project.localInitInstructionMd)
  const deleteProject = {
    id: projectId,
    projectName: (project && project.projectName) || source.projectName || '',
    projectSlug: (project && project.projectSlug) || '',
    status: (project && project.status) || source.status || '',
    projectKind: (project && project.projectKind) || source.projectKind || 'dataset'
  }

  const startEditDescription = () => {
    if (descriptionSaving) return
    setDescriptionDraft(description)
    setDescriptionEditing(true)
  }

  const cancelEditDescription = () => {
    if (descriptionSaving) return
    setDescriptionEditing(false)
    setDescriptionDraft('')
  }

  const saveDescription = () => {
    if (descriptionSaving) return
    const nextDescription = (descriptionDraft || '').trim()
    if (!nextDescription) {
      message.warning('项目简介不能为空')
      return
    }
    setDescriptionSaving(true)
    saveMyVibeProjectDescription({ projectId, description: nextDescription })
      .then(saved => {
        if (!mountedRef.current) return
        const savedDescription = (saved && saved.description) || nextDescription
        setProject(prev => ({ ...(prev || {}), description: savedDescription }))
        setDescriptionEditing(false)
        setDescriptionDraft('')
        message.success('项目简介已保存，数据说明已同步')
      })
      .catch(() => {})
      .finally(() => {
        if (mountedRef.current) setDescriptionSaving(false)
      })
  }

  const saveAccessType = nextAccessType => {
    if (accessTypeSaving) return
    setAccessTypeSaving(true)
    saveMyVibeProjectAccessType({ projectId, accessType: nextAccessType })
      .then(() => {
        if (!mountedRef.current) return
        setProject(prev => ({ ...(prev || {}), accessType: nextAccessType }))
        message.success(nextAccessType === 'EXTERNAL' ? '已改为外部用户访问' : '已改为内部员工访问')
      })
      .catch(() => {})
      .finally(() => {
        if (mountedRef.current) setAccessTypeSaving(false)
      })
  }

  const changeAccessType = nextAccessType => {
    if (nextAccessType === accessType) return
    if (nextAccessType !== 'EXTERNAL') {
      saveAccessType(nextAccessType)
      return
    }
    Modal.confirm({
      title: '确认改为外部用户访问？',
      content: <div className="term-ops-confirm-danger">切换后，外部用户访问时不会检查员工权限，请确认该项目确实面向外部用户。</div>,
      okText: '确认切换',
      okType: 'danger',
      cancelText: '取消',
      onOk: () => saveAccessType(nextAccessType)
    })
  }

  const retryProject = () => {
    if (retryLoading) return
    setRetryLoading(true)
    retryMyVibeProject({ id: projectId })
      .then(result => {
        if (!mountedRef.current) return
        if (result && typeof result === 'object') {
          setProject(prev => ({ ...(prev || {}), ...result }))
        }
        message.success('已重新执行接入')
        // 接入是长链路：只刷新本分区状态，再把详情交回上层刷新，不做额外轮询。
        loadDetail()
        if (onUpdated) onUpdated()
      })
      .catch(() => {})
      .finally(() => {
        if (mountedRef.current) setRetryLoading(false)
      })
  }

  const copyInstruction = () => {
    if (!instruction) {
      message.warning('暂无可复制内容')
      return
    }
    if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(instruction)
        .then(() => message.success('已复制提示词'))
        .catch(() => message.warning('复制失败，请手动复制'))
      return
    }
    message.warning('当前浏览器不支持自动复制，请手动复制')
  }

  const closeDelete = () => setDeleteVisible(false)

  const handleDeleted = () => {
    setDeleteVisible(false)
    // P5：删除弹窗内部已提示「项目已彻底删除」，这里不再重复 toast。
    // 顺序固定为「先通知上层刷新台账 → 再关闭详情浮层」，避免先卸载导致刷新回调丢失。
    if (onDeleted) onDeleted()
    if (onClose) onClose()
  }

  return <section className="term-ops-section">
    {head}

    <div className="term-ops-card">
      <div className="term-ops-card-head">
        <span className="term-ops-card-title">项目状态</span>
        <Tag className="term-ops-status-tag" color={statusMeta.color}>{statusMeta.label}</Tag>
        {failed && isApplication
          ? <button
            type="button"
            className="term-primary term-ops-retry-inline"
            disabled={retryLoading}
            onClick={retryProject}
          >
            {retryLoading ? '重试中…' : '重试接入'}
          </button>
          : null}
      </div>
      <div className="term-ops-meta">
        {failed
          ? (isApplication
            ? '项目接入失败，可重试接入；重复失败请先排查下方失败原因。'
            : '项目接入失败，请先排查下方失败原因；AIC 数据集项目不提供看板内重试。')
          : '项目接入状态来自 Vibe 项目详情；只有接入失败时才提供重试。'}
      </div>
      {failed && provisionError
        ? <React.Fragment>
          <div className="term-ops-label">接入失败原因</div>
          <pre className="term-ops-pre">{provisionError}</pre>
        </React.Fragment>
        : null}
      {failed && mysqlDatabaseError
        ? <React.Fragment>
          <div className="term-ops-label">数据库配置失败原因</div>
          <pre className="term-ops-pre">{mysqlDatabaseError}</pre>
        </React.Fragment>
        : null}
    </div>

    <div className="term-ops-card">
      <div className="term-ops-card-head">
        <span className="term-ops-card-title">项目简介</span>
        {descriptionEditing
          ? null
          : <button type="button" className="term-link term-ops-head-action" onClick={startEditDescription}>编辑</button>}
      </div>
      {descriptionEditing
        ? <React.Fragment>
          <Input.TextArea
            className="term-ops-textarea"
            value={descriptionDraft}
            maxLength={500}
            autoSize={{ minRows: 3, maxRows: 8 }}
            placeholder="说明这个项目能查什么、解决什么问题"
            onChange={event => setDescriptionDraft(event.target.value)}
          />
          <div className="term-ops-actions">
            <button type="button" className="term-primary" disabled={descriptionSaving} onClick={saveDescription}>
              {descriptionSaving ? '保存中…' : '保存'}
            </button>
            <button type="button" className="term-ghost" disabled={descriptionSaving} onClick={cancelEditDescription}>取消</button>
            <span className="term-ops-note">最多 500 字，保存后同步更新数据说明。</span>
          </div>
        </React.Fragment>
        : <div className={`term-ops-description${description ? '' : ' term-ops-description-empty'}`}>
          {description || '未填写项目简介'}
        </div>}
    </div>

    {isApplication ? <div className="term-ops-card">
      <div className="term-ops-card-head"><span className="term-ops-card-title">访问对象</span></div>
      <div className="term-ops-meta">内部员工访问会检查员工权限；外部用户访问不检查员工权限，切换前会二次确认。</div>
      <Radio.Group
        className="term-ops-radio"
        value={accessType}
        disabled={accessTypeSaving}
        onChange={event => changeAccessType(event.target.value)}
      >
        <Radio value="INTERNAL">内部员工访问</Radio>
        <Radio value="EXTERNAL">外部用户访问</Radio>
      </Radio.Group>
      {accessTypeSaving ? <div className="term-ops-loading"><Spin size="small" /> 保存中…</div> : null}
    </div> : null}

    {instruction
      ? <div className="term-ops-card">
        <div className="term-ops-card-head">
          <span className="term-ops-card-title">初始化提示词</span>
          <button type="button" className="term-link term-ops-head-action" onClick={copyInstruction}>复制提示词</button>
        </div>
        <Collapse bordered={false} className="term-ops-collapse" defaultActiveKey={[]}>
          <Collapse.Panel key="prompt" header="展开查看初始化提示词">
            <pre className="term-ops-prompt">{instruction}</pre>
          </Collapse.Panel>
        </Collapse>
      </div>
      : null}

    <div className="term-ops-card term-ops-danger">
      <div className="term-ops-card-head"><span className="term-ops-card-title">危险操作</span></div>
      <div className="term-ops-meta">
        物理删除会清理项目运行环境、数据、权限、文件与业务元数据，不可恢复；删除前会先做资源预检并要求输入项目 Slug 二次确认。
      </div>
      <div className="term-ops-actions">
        <button type="button" className="term-ops-danger-btn" onClick={() => setDeleteVisible(true)}>物理删除项目</button>
        <span className="term-ops-note">删除任务在后台异步执行，可在弹窗内查看进度。</span>
      </div>
    </div>

    <VibeProjectDeleteDialog
      visible={deleteVisible}
      project={deleteProject}
      onClose={closeDelete}
      onDeleted={handleDeleted}
    />
  </section>
}
